import { set } from 'lodash/fp';

import { isUpdateInquiryNextStepActionStep } from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { createMockActionStep } from 'dashboard/lib/stubs/inquiry-workflow-version';
import {
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { captureMessage } from 'lib/reporting';

/**
 * Emulates findAndReplaceSteps() in WorkflowEditor
 * Updates ITE workflow version(s) workflowSteps when inserting a new screen step
 * after an action sequence or branching node by updating the next_step_name param
 * or inserting a new such action step.
 *
 * Recursively searches for targetStepToken and calls the workflowStepsNextUpdated() helper to update,
 * If target token is a conditional branch, update the workflowSteps in the given branchIndex.
 * Otherwise, update the transition step in the same branch of a matching token.
 *
 * @returns workflow version's workflowSteps with updated next_step_name or new update_next_step action
 */
export const workflowStepsUpdated = (
  workflowSteps: WorkflowStep[],
  targetStepToken: string,
  branchIndex: number | null,
  nextStepName: string,
): WorkflowStep[] => {
  // ignore parallel branches in this function as they do not connect to screen workflowSteps
  const conditionalIndex = workflowSteps.findIndex(
    (step) => step.type === WorkflowStepType.Conditional,
  );
  if (conditionalIndex !== -1) {
    if (conditionalIndex !== workflowSteps.length - 1) {
      captureMessage(`Can't update branch where conditional step is not last step`, 'warning');
      return workflowSteps;
    }
    const conditionalStep = workflowSteps[conditionalIndex] as WorkflowStepConditional;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let updatedStep: WorkflowStepConditional = null;
    if (conditionalStep.token === targetStepToken) {
      if (branchIndex == null || branchIndex >= conditionalStep.branches.length) {
        captureMessage('Branch index out of bounds', 'warning');
        return workflowSteps;
      }
      updatedStep = {
        ...conditionalStep,
        branches: set(
          [branchIndex],
          workflowStepsNextUpdated(conditionalStep.branches[branchIndex], nextStepName),
          conditionalStep.branches,
        ),
      };
    } else {
      updatedStep = {
        ...conditionalStep,
        branches: conditionalStep.branches.map((branchSteps) =>
          workflowStepsUpdated(branchSteps, targetStepToken, branchIndex, nextStepName),
        ),
      };
    }
    return set([workflowSteps.length - 1], updatedStep, workflowSteps);
  } else {
    return workflowSteps.find((step) => step.token === targetStepToken)
      ? workflowStepsNextUpdated(workflowSteps, nextStepName)
      : workflowSteps;
  }
};

/**
 * Helper used primarily in InquiryTemplateFlowEditor when inserting
 * a new screen step after an action sequence or branching node
 *
 * Updates next_step_name for the update_inquiry_next_step_name action
 * in the previous workflow branch relative to the insertion index of the new step.
 *
 * Note: assumes the presense of EXACTLY ONE update_next_step action,
 * as this is currently the only action that a workflow can use to transition the inquiry.
 * Inserts new update_next_step action step if none exists.
 *
 * @returns workflow workflowSteps branch with updated next_step_name in the update_inquiry_next_step_name action
 */
export const workflowStepsNextUpdated = (
  workflowSteps: WorkflowStep[],
  nextStepName: string,
): WorkflowStep[] => {
  const stepIndex = workflowSteps.findIndex(isUpdateInquiryNextStepActionStep);
  if (stepIndex !== -1) {
    const updatedStep = set(
      ['action', 'params', 'next-step-name'],
      nextStepName,
      workflowSteps[stepIndex],
    );
    return set([stepIndex], updatedStep, workflowSteps);
  } else {
    return workflowSteps.concat([createMockActionStep('route', nextStepName)]);
  }
};
